import { useState, useRef, useCallback } from "react"
import type { ICommune, IParcel } from "@/types/explore"
import { fetchCommuneContour } from "@/lib/explore/api/geo"
import { getKaptcherCommuneData } from "@/lib/explore/api/kaptcher-data"
import { fetchPluDocument } from "@/lib/explore/api/plu"

interface CommuneRecord {
  code_com: string
  code_dep: string
  nom: string
  latitude: number
  longitude: number
}

interface UseCommuneSelectionProps {
  selectedParcels: IParcel[]
  communes?: CommuneRecord[]
}

export const useCommuneSelection = (props: UseCommuneSelectionProps) => {
  const { selectedParcels, communes = [] } = props
  const [selectedCommune, setSelectedCommune] = useState<ICommune | undefined>(undefined)
  const [showCommuneWarning, setShowCommuneWarning] = useState(false)
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onSelectCommune = useCallback(
    async (code: string) => {
      if (!code) return

      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
        warningTimeoutRef.current = null
      }

      const record = communes.find((c) => c.code_com === code)

      // Set basic data immediately so the UI is responsive
      const basicData: Partial<ICommune> = {
        code: code,
        libelle: record?.nom ?? code,
        nom: record?.nom,
        codeDepartement: record?.code_dep,
        centre: record ? { type: "Point", coordinates: [record.longitude, record.latitude] } : undefined,
      }
      setSelectedCommune(basicData as ICommune)

      try {
        // Fetch full commune data (INSEE + DVF) from DB
        const communeData = await getKaptcherCommuneData(code)
        setSelectedCommune(communeData)

        if (selectedParcels.length > 0) {
          warningTimeoutRef.current = setTimeout(() => {
            const hasMismatch = selectedParcels.some((p) => p.properties?.nom_com !== communeData?.libelle)
            if (hasMismatch) setShowCommuneWarning(true)
            warningTimeoutRef.current = null
          }, 350)
        }

        // Fetch PLU document info in background using commune center coordinates
        const coords = communeData?.centre?.coordinates
        if (coords) {
          fetchPluDocument(coords[0], coords[1]).then((pluDoc) => {
            if (pluDoc) {
              setSelectedCommune((prev) =>
                prev?.code === code ? ({ ...prev, pluDocument: pluDoc } as ICommune) : prev,
              )
            }
          })
        }
      } catch (error) {
        console.error("Error fetching commune data:", error)
      }

      // Fetch contour in background
      fetchCommuneContour(code).then((contour) => {
        if (contour) {
          setSelectedCommune((prev) => (prev?.code === code ? ({ ...prev, contour } as ICommune) : prev))
        }
      })
    },
    [selectedParcels, communes],
  )

  return {
    onSelectCommune,
    selectedCommune,
    setSelectedCommune,
    showCommuneWarning,
    setShowCommuneWarning,
  }
}
